import { cn } from 'component-library';
import { useTranslation } from 'react-i18next';

import {
  CLIMATE_RISK_FACT_TYPES,
  ClimateRiskRatingOrder,
  PlotReportFactLabelNameEnum,
  Scenario,
} from '@/api/rest/resources/types/fact';
import { DefaultWidgetNames, WidgetSlotConfiguration, WidgetType } from '@/api/rest/resources/types/widget';
import { useOrganizationIndicatorReportingFact } from '@/pages/shared/hooks/useOrganizationFact';
import { isNullish } from '@/utils/booleans';
import { safeRiskIndexToRating } from '@/utils/climateRisk';

type RiskAggregatedOverviewProps = {
  styles?: Record<string, string>;
};

export const useRiskAggregatedOverviewConfiguration = (props?: RiskAggregatedOverviewProps) => {
  const { t } = useTranslation();

  const facts = useOrganizationIndicatorReportingFact(Object.keys(CLIMATE_RISK_FACT_TYPES), null, {
    [PlotReportFactLabelNameEnum.scenario]: Scenario.baseline,
  });

  let risks = Object.keys(facts)
    .filter((factType) => {
      const risk = facts[`${factType}`];
      return !isNullish(risk?.value);
    })
    .map((factType) => facts[`${factType}`])
    .sort((a, b) => {
      const aRating = safeRiskIndexToRating(a?.value as number);
      const bRating = safeRiskIndexToRating(b?.value as number);
      // eslint-disable-next-line security/detect-object-injection
      return ClimateRiskRatingOrder[aRating] > ClimateRiskRatingOrder[bRating] ? -1 : 1;
    });

  // Limit to 7 risks for the chart
  risks = risks.slice(0, 7);
  return {
    name: DefaultWidgetNames.default_climate_risk_aggregated_chart,
    type: WidgetType.o_card,
    visible: true,
    relatedData: {
      header: t('climateRisk.riskOverview'),
      headerTooltip: t('climateRisk.riskOverviewTooltip.body'),
      styles: {
        root: cn('h-full rounded-2xl w-full', props?.styles?.root ?? ''),
        header: cn(
          'typography-overline pt-4 pl-4 justify-center items-center text-text-secondary border-none',
          props?.styles?.header ?? '',
        ),
        body: cn('h-full flex flex-col justify-center gap-4 p-8 border-none', props?.styles?.body ?? ''),
      },
    },
    widgets: [
      {
        name: 'climate_risk_sector_chart',
        type: WidgetType.o_c_risk_sector_chart,
        visible: true,
        relatedData: {
          risks,
        },
      },
    ],
  } as WidgetSlotConfiguration;
};
